// AstriaMexicoV2Shell.tsx
import React from "react";
import { useLaneCopy } from "../../../utils/laneCopy";
import { useNavigate } from "react-router-dom";
import { Moon, Heart, Hourglass, CloudSun, Compass, NotebookPen } from "lucide-react";
import { MexicoV2Lang } from "./mexicoV2Language";
import { MEXICO_V2_COPY } from "./mexicoV2Copy";

// ─── Astria Mexico v2 — shared shell (header + sticky tab bar) ────────────
// Per client spec (BrMaxico.txt — "MX Lane v2 — UI JSON (Warm • Expressive •
// Grounded)"): warm, expressive, grounded identity. All values below are
// taken verbatim from the client's MX v2 UI JSON pack — palette
// (primary/secondary/accent/background_light/background_dark/text_primary/
// text_secondary), typography (line_height 1.46), layout (card_radius 14,
// card_shadow, max_width 720), modules (reflection/emotional_thread/
// gentle_note), buttons (share_whatsapp/share_instagram/copy/save), and
// navigation (active_color/inactive_color) — see MexicoV2Card.tsx for the
// modules/buttons usage. Distinct from BR v2's yellow/orange, but same
// structural pattern (see AstriaBrazilV2Shell.tsx / AstriaVietnamV2Shell.tsx).
// 6 fixed tabs, no birth chart, no LLM — see helper/astriaMexicoV2Service.js
// on the backend.
//
// Bilingual (EN/ES) — UI chrome text comes from mexicoV2Copy.ts in the user's
// preferred language (see mexicoV2Language.ts); there is no per-screen toggle.

export const MEXICO_V2_HEADING_FONT = "'Inter', sans-serif";
export const MEXICO_V2_BODY_FONT = "'Inter', sans-serif";

// Client JSON: ui_identity.layout specifies max_width 720, but that leaves
// large empty side margins on wide chat panels — widened to match the other
// shells (Brazil/Vietnam/India/Indonesia/Philippines V2 all use 960) for
// visual consistency across lanes.
export const MEXICO_V2_MAX_WIDTH = 960;
export const MEXICO_V2_CARD_RADIUS = 14;
export const MEXICO_V2_CARD_SHADOW = "0px 2px 8px rgba(0,0,0,0.08)";
// Client JSON: ui_identity.typography.line_height
export const MEXICO_V2_LINE_HEIGHT = 1.46;

export const SHELL_COLORS_MEXICO_V2 = {
  // ui_identity.palette
  bg: "linear-gradient(160deg, #FFFFFF 0%, #FFF4E6 100%)",
  card: "#FFFFFF",
  section: "#FFF4E6",
  cardBorder: "#FBD9BC",
  primary: "#FF6F4E",
  secondary: "#FFB347",
  accentColor: "#4CAF50",
  backgroundLight: "#FFF4E6",
  backgroundDark: "#2A2A2A",
  titleColor: "#2D2D2D",
  sectionTitleColor: "#2D2D2D",
  bodyColor: "#2D2D2D",
  mutedColor: "#5A5A5A",
  // aliases kept for the shared wizard/result-card component patterns
  accent: "#FF6F4E",
  accentSoft: "rgba(255,111,78,0.16)",
  accentGlow: "0 0 0 3px rgba(255,111,78,0.18)",
  amber: "#FFB347",
  amberSoft: "rgba(255,179,71,0.14)",
  // WizardColors (AstriaIndiaV2Wizard.tsx) expects `gold` as its secondary
  // gradient stop — alias to the same amber used elsewhere in this palette.
  gold: "#FFB347",
  green: "#4CAF50",
  dark: "#2A2A2A",
  white: "#FFFFFF",
  // navigation.active_color / navigation.inactive_color
  navActive: "#FF6F4E",
  navInactive: "#5A5A5A",
};

// ui_identity's three named "modules" (used by MexicoV2Card.tsx's result
// card sections) and the four named "buttons" — copied verbatim from the
// client's MX v2 UI JSON.
export const MEXICO_V2_MODULES = {
  reflection: {
    headerColor: "#FF6F4E",
    bodyColor: "#FFF4E6",
    textColor: "#2D2D2D",
  },
  emotional_thread: {
    headerColor: "#FFB347",
    bodyColor: "#FFF8E1",
    textColor: "#2D2D2D",
  },
  gentle_note: {
    headerColor: "#4CAF50",
    bodyColor: "#F1F8E9",
    textColor: "#2D2D2D",
  },
};

export const MEXICO_V2_BUTTONS = {
  share_whatsapp: { color: "#4CAF50", textColor: "#FFFFFF", radius: 12 },
  share_instagram: { color: "#FF6F4E", textColor: "#FFFFFF", radius: 12 },
  copy: { color: "#FFB347", textColor: "#2D2D2D", radius: 12 },
  save: { color: "#2A2A2A", textColor: "#FFFFFF", radius: 12 },
};

export type AstriaMexicoV2Tab =
  | "soft_summary"
  | "connection_atmosphere"
  | "timing_rhythm"
  | "emotion_balance"
  | "guidance_path"
  | "your_note";

interface TabDef {
  key: AstriaMexicoV2Tab;
  // The SubCategory name exactly as seeded by
  // scripts/createAstriaMexicoV2Category.js — always English, used to
  // resolve categoryId/subCategoryId in useMexicoV2Tab.ts regardless of
  // the active display language. Never render this directly; use the
  // language-keyed copy.tabBar[key].label for display.
  dbName: string;
  icon: React.ReactNode;
  path: string;
  accent: [string, string];
}

// Icons/order per BrMaxico.txt's MX v2 UI pack: 🌙 💛 ⏳ 🌤️ 🧭 📝
const TABS: TabDef[] = [
  {
    key: "soft_summary",
    dbName: "Soft Summary",
    icon: <Moon className="w-4 h-4" />,
    path: "/astria-mexico-v2/soft-summary",
    accent: ["#FF6F4E", "#FFB347"],
  },
  {
    key: "connection_atmosphere",
    dbName: "Connection Atmosphere",
    icon: <Heart className="w-4 h-4" />,
    path: "/astria-mexico-v2/connection-atmosphere",
    accent: ["#FFB347", "#FF6F4E"],
  },
  {
    key: "timing_rhythm",
    dbName: "Timing Rhythm",
    icon: <Hourglass className="w-4 h-4" />,
    path: "/astria-mexico-v2/timing-rhythm",
    accent: ["#FF6F4E", "#4CAF50"],
  },
  {
    key: "emotion_balance",
    dbName: "Emotion Balance",
    icon: <CloudSun className="w-4 h-4" />,
    path: "/astria-mexico-v2/emotion-balance",
    accent: ["#4CAF50", "#FF6F4E"],
  },
  {
    key: "guidance_path",
    dbName: "Guidance Path",
    icon: <Compass className="w-4 h-4" />,
    path: "/astria-mexico-v2/guidance-path",
    accent: ["#FF6F4E", "#FFB347"],
  },
  {
    key: "your_note",
    dbName: "Your Note",
    icon: <NotebookPen className="w-4 h-4" />,
    path: "/astria-mexico-v2/your-note",
    accent: ["#FFB347", "#4CAF50"],
  },
];

interface AstriaMexicoV2ShellProps {
  active: AstriaMexicoV2Tab;
  lang: MexicoV2Lang;
  children: React.ReactNode;
}

const AstriaMexicoV2Shell: React.FC<AstriaMexicoV2ShellProps> = ({
  active,
  lang,
  children,
}) => {
  const navigate = useNavigate();
  const copy = useLaneCopy("v2", MEXICO_V2_COPY, lang);

  return (
    <div
      className="min-h-screen"
      style={{
        background: SHELL_COLORS_MEXICO_V2.bg,
        fontFamily: MEXICO_V2_BODY_FONT,
      }}
    >
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          background: "rgba(255,251,248,0.82)",
          borderBottom: `1px solid ${SHELL_COLORS_MEXICO_V2.cardBorder}`,
        }}
      >
        <div
          className="mx-auto flex items-center gap-3 px-5"
          style={{ maxWidth: MEXICO_V2_MAX_WIDTH, paddingTop: 18, paddingBottom: 14 }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${SHELL_COLORS_MEXICO_V2.accent} 0%, ${SHELL_COLORS_MEXICO_V2.amber} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 14px ${SHELL_COLORS_MEXICO_V2.accentSoft}`,
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 16, color: "#fff" }}>💛</span>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: SHELL_COLORS_MEXICO_V2.titleColor,
                fontFamily: MEXICO_V2_HEADING_FONT,
                letterSpacing: "0.01em",
              }}
            >
              {copy.shell.title}
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: SHELL_COLORS_MEXICO_V2.mutedColor,
              }}
            >
              {copy.shell.subtitle}
            </div>
          </div>

        </div>

        {/* ─── Tab bar ───────────────────────────────────────────────── */}
        <div style={{ position: "relative" }}>
          <div
            className="mx-auto px-5 pb-3 hide-scrollbar"
            style={{
              maxWidth: MEXICO_V2_MAX_WIDTH,
              display: "flex",
              gap: 8,
              overflowX: "auto",
              scrollbarWidth: "none",
            }}
          >
            {TABS.map((tab) => {
              const isActive = tab.key === active;
              return (
                <button
                  key={tab.key}
                  onClick={() => navigate(tab.path)}
                  className="flex items-center gap-1.5 rounded-full transition-all whitespace-nowrap"
                  style={{
                    paddingInline: 14,
                    paddingBlock: 8,
                    fontSize: 12.5,
                    fontWeight: 600,
                    flexShrink: 0,
                    // navigation.active_color / navigation.inactive_color
                    color: isActive
                      ? "#FFFFFF"
                      : SHELL_COLORS_MEXICO_V2.navInactive,
                    background: isActive
                      ? SHELL_COLORS_MEXICO_V2.navActive
                      : SHELL_COLORS_MEXICO_V2.section,
                    boxShadow: isActive
                      ? `0 4px 14px ${SHELL_COLORS_MEXICO_V2.accentSoft}`
                      : "none",
                    border: isActive
                      ? "none"
                      : `1px solid ${SHELL_COLORS_MEXICO_V2.cardBorder}`,
                  }}
                >
                  {tab.icon}
                  <span>{copy.tabBar[tab.key].label}</span>
                </button>
              );
            })}
          </div>
          {/* Right-edge fade so a clipped last tab reads as "scroll for
              more" rather than a layout bug — the row has no scrollbar
              (hidden above) so this is the only affordance. */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 12,
              width: 28,
              pointerEvents: "none",
              background:
                "linear-gradient(90deg, rgba(255,251,248,0) 0%, rgba(255,251,248,0.9) 100%)",
            }}
          />
        </div>
      </div>

      {/* ─── Body ────────────────────────────────────────────────────── */}
      <div
        className="mx-auto px-4 sm:px-5"
        style={{ maxWidth: MEXICO_V2_MAX_WIDTH, paddingTop: 24, paddingBottom: 64 }}
      >
        {children}
      </div>
    </div>
  );
};

export default AstriaMexicoV2Shell;
export { TABS as ASTRIA_MEXICO_V2_TABS };
